import React, { useState, useEffect } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import Layout from '../components/Layout';
import { HashtagChip, StatusBadge, CategoryBadge } from '../components/Badges';
import { BookOpen, Search, Pin, PinOff, Plus, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function KnowledgeBasePage() {
  const { lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newArticle, setNewArticle] = useState({ title_fr: '', title_en: '', content_fr: '', content_en: '', category: '', hashtags: [] });
  const [hashtagInput, setHashtagInput] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchArticles = async () => {
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (searchTerm) params.search = searchTerm;
      const { data } = await api.get('/kb', { params });
      setArticles(data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchArticles(); }, [filterCategory, searchTerm]);
  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data)).catch(() => {});
  }, []);

  const togglePin = async (articleId) => {
    try { await api.put('/kb/' + articleId + '/pin'); fetchArticles(); } catch {}
  };

  const deleteArticle = async (e, articleId) => {
    e.stopPropagation();
    if (!window.confirm(lang === 'fr' ? 'Supprimer cet article ?' : 'Delete this article?')) return;
    try { await api.delete('/kb/' + articleId); fetchArticles(); } catch {}
  };

  const addHashtag = () => {
    const tag = hashtagInput.trim().toLowerCase().replace(/^#/, '');
    if (tag && !newArticle.hashtags.includes(tag)) {
      setNewArticle({ ...newArticle, hashtags: [...newArticle.hashtags, tag] });
    }
    setHashtagInput('');
  };

  const handleCreateArticle = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/kb', newArticle);
      setShowCreateForm(false);
      setNewArticle({ title_fr: '', title_en: '', content_fr: '', content_en: '', category: categories[0]?.name || '', hashtags: [] });
      fetchArticles();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating article');
    } finally { setCreating(false); }
  };

  const isAdmin = user?.role === 'direction' || user?.role === 'support';

  return (
    <Layout>
      <div className="animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <BookOpen size={22} style={{ color: '#F77F00', flexShrink: 0 }} />
            <h1 data-testid="kb-title" className="text-xl sm:text-2xl font-bold text-white truncate">{t('knowledge_base', lang)}</h1>
            <span data-testid="kb-article-count" className="text-xs sm:text-sm px-2.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0" style={{ background: 'rgba(247,127,0,0.15)', color: '#F77F00' }}>
              {articles.length} {t('articles', lang)}
            </span>
          </div>
          {isAdmin && (
            <button data-testid="create-kb-article" onClick={() => setShowCreateForm(!showCreateForm)} className="apollo-btn-primary flex items-center gap-2 flex-shrink-0" aria-label={lang === 'fr' ? 'Ajouter un article' : 'Add article'} style={{ minHeight: '44px' }}>
              <Plus size={18} aria-hidden="true" /> <span className="hidden sm:inline">{lang === 'fr' ? 'Ajouter un article' : 'Add article'}</span><span className="sm:hidden">{lang === 'fr' ? 'Ajouter' : 'Add'}</span>
            </button>
          )}
        </div>

        {/* Create Article Form (Direction only) */}
        {showCreateForm && isAdmin && (
          <div className="apollo-card p-4 sm:p-6 mb-6 animate-fade-in">
            <h3 className="font-semibold text-white mb-4">{lang === 'fr' ? 'Nouvel article' : 'New article'}</h3>
            <form onSubmit={handleCreateArticle} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: '#9CA3AF' }}>{t('title', lang)} (FR)</label>
                  <input value={newArticle.title_fr} onChange={e => setNewArticle({ ...newArticle, title_fr: e.target.value })} className="apollo-input" style={{ minHeight: '44px' }} required />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: '#9CA3AF' }}>{t('title', lang)} (EN)</label>
                  <input value={newArticle.title_en} onChange={e => setNewArticle({ ...newArticle, title_en: e.target.value })} className="apollo-input" style={{ minHeight: '44px' }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#9CA3AF' }}>{t('category', lang)}</label>
                <select value={newArticle.category} onChange={e => setNewArticle({ ...newArticle, category: e.target.value })} className="apollo-input w-full" style={{ minHeight: '44px', background: '#0F0F1A', cursor: 'pointer' }}>
                  <option value="">--</option>
                  {categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#9CA3AF' }}>{t('description', lang)} (FR)</label>
                <textarea value={newArticle.content_fr} onChange={e => setNewArticle({ ...newArticle, content_fr: e.target.value })} className="apollo-input min-h-[120px] w-full" required />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#9CA3AF' }}>{t('description', lang)} (EN)</label>
                <textarea value={newArticle.content_en} onChange={e => setNewArticle({ ...newArticle, content_en: e.target.value })} className="apollo-input min-h-[120px] w-full" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#9CA3AF' }}>{t('hashtags', lang)}</label>
                <div className="flex gap-2 flex-wrap mb-2">
                  {newArticle.hashtags.map(tag => (
                    <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs" style={{ background: 'rgba(247,127,0,0.12)', color: '#F77F00' }}>
                      #{tag} <button type="button" onClick={() => setNewArticle({ ...newArticle, hashtags: newArticle.hashtags.filter(h => h !== tag) })}><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={hashtagInput} onChange={e => setHashtagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addHashtag(); } }} className="apollo-input flex-1" placeholder="#..." style={{ minHeight: '44px' }} />
                  <button type="button" onClick={addHashtag} className="apollo-btn-secondary px-4" style={{ minHeight: '44px', minWidth: '44px' }}>+</button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button type="submit" disabled={creating} className="apollo-btn-primary" style={{ minHeight: '44px' }}>{creating ? '...' : (lang === 'fr' ? 'Créer' : 'Create')}</button>
                <button type="button" onClick={() => setShowCreateForm(false)} className="apollo-btn-secondary" style={{ minHeight: '44px' }}>{t('cancel', lang)}</button>
              </div>
            </form>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-6">
          <div className="relative flex-1 sm:max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#6B7280' }} />
            <input data-testid="kb-search" type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="apollo-input w-full" placeholder={t('search', lang)} style={{ minHeight: '44px', paddingLeft: '40px' }} />
          </div>
          <select data-testid="kb-filter-category" value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="apollo-input w-full sm:w-auto" style={{ minHeight: '44px', background: '#1A1A2E', cursor: 'pointer' }}>
            <option value="">{t('all_categories', lang)}</option>
            {categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </div>

        {loading ? (
          <p className="text-center py-12" style={{ color: '#6B7280' }}>{t('loading', lang)}</p>
        ) : articles.length === 0 ? (
          <p className="text-center py-16" style={{ color: '#6B7280' }}>{lang === 'fr' ? 'Aucun article trouvé' : 'No articles found'}</p>
        ) : (
          <div className="space-y-4">
            {articles.map((article, idx) => (
              <div key={article._id} data-testid={"kb-article-" + article._id}
                className="apollo-card p-4 sm:p-5 animate-fade-in cursor-pointer"
                style={{ animationDelay: idx * 40 + 'ms' }}
                onClick={() => navigate('/tickets/' + article.ticket_id)}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {article.pinned && <Pin size={14} style={{ color: '#F77F00', flexShrink: 0 }} />}
                      <h3 className="font-semibold text-white text-sm sm:text-base truncate">
                        {lang === 'en' ? (article.ticket?.title_en || article.ticket?.title_fr) : (article.ticket?.title_fr || article.ticket?.title_en)}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <CategoryBadge category={lang === 'en' ? t(article.ticket?.category, lang) : article.ticket?.category} />
                      <StatusBadge status={article.ticket?.status} />
                      {article.ticket?.hashtags?.map(tag => <HashtagChip key={tag} tag={tag} />)}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button data-testid={"pin-" + article._id} onClick={(e) => { e.stopPropagation(); togglePin(article._id); }} className="rounded-lg hover:bg-white/5 flex items-center justify-center" style={{ color: '#F77F00', width: 44, height: 44 }} aria-label="Pin">
                        {article.pinned ? <PinOff size={16} /> : <Pin size={16} />}
                      </button>
                      <button data-testid={"delete-" + article._id} onClick={(e) => deleteArticle(e, article._id)} className="rounded-lg hover:bg-white/5 flex items-center justify-center" style={{ color: '#EF4444', width: 44, height: 44 }} aria-label="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs sm:text-sm mb-3 line-clamp-3" style={{ color: '#E8E8E8' }}>
                  {lang === 'en' ? (article.ticket?.description_en || article.ticket?.description_fr) : (article.ticket?.description_fr || article.ticket?.description_en)}
                </p>
                <div className="flex items-center gap-2 sm:gap-4 text-xs flex-wrap" style={{ color: '#6B7280' }}>
                  <span className="truncate max-w-full">{article.ticket?.created_by_name}</span>
                  <span>{new Date(article.ticket?.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</span>
                  <span>{article.replies?.length || 0} {t('reply', lang)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
